import { Mark, Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * نودهای اختصاصی فرمول ریاضی ادیتور
 */
const MATH_NODES = new Set([
  'math',
  'fraction',
  'mixedFraction',
  'sqrt',
  'nthRoot',
  'power',
  'subscript',
]);

/**
 * تشخیص قطعی حروف الفبای فارسی/عربی (بدون ارقام و نمادهای ریاضی)
 */
const isFaLetter = (char) => {
  if (!char) return false;
  // ارقام فارسی و عربی نباید جزو حروف شمرده شوند
  if (/[0-9\u06F0-\u06F9\u0660-\u0669]/.test(char)) return false;
  // نمادهای ریاضی و علائم نگارشی نباید جزو حروف شمرده شوند
  if (/[+\-−×÷*/=≠±∓<>≤≥≈~≡≅^_%\u00B0′″‰√∛∜π∑∏∫∂∞∠△⊥∥()[\]{}|\\.,:;!?،؟؛]/.test(char)) return false;
  // بررسی حروف الفبای فارسی و عربی
  return /[\u0620-\u064A\u0671-\u06D3\u06FB-\u06FE\u064B-\u065F\u0670\u067E\u0686\u0698\u06A9\u06AF\u06CC]/.test(char);
};

const DIGIT_REGEX = /[0-9\u06F0-\u06F9\u0660-\u0669]/;
const OPERATOR_REGEX = /[+\-−×÷*/=≠±∓<>≤≥≈~≡≅^_%\u00B0′″‰√∛∜π∑∏∫∂∞∠△⊥∥]/;
const BRACKET_REGEX = /[()[\]{}|]/;
const LATIN_VAR_REGEX = /[a-zA-Z]/;
const SPACE_REGEX = /[\s\u00A0]/;

const getCharType = (char) => {
  if (isFaLetter(char)) return 'FA_TEXT';
  if (DIGIT_REGEX.test(char)) return 'DIGIT';
  if (OPERATOR_REGEX.test(char)) return 'OPERATOR';
  if (BRACKET_REGEX.test(char)) return 'BRACKET';
  if (LATIN_VAR_REGEX.test(char)) return 'LATIN';
  if (SPACE_REGEX.test(char)) return 'SPACE';
  if (char === '.' || char === ',' || char === '/' || char === '٫' || char === '٬') return 'SEPARATOR';
  return 'OTHER';
};

export const MathExpressionMark = Mark.create({
  name: 'mathExpr',
  inclusive: false, // غیرفعال بودن ارث‌بری حین تایپ حروف جدید در مرزها
  parseHTML() {
    return [
      { tag: 'span[data-math-expr]' },
      { tag: 'span.math-expression-wrap' },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        'data-math-expr': 'true',
        class: 'math-expression-wrap',
        dir: 'ltr',
        style:
          'direction: ltr; unicode-bidi: isolate; display: inline-block; vertical-align: middle; margin: 0 2px;',
      },
      0,
    ];
  },
});

export const MathDirectionExtension = Extension.create({
  name: 'mathDirection',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('mathDirection'),
        appendTransaction(transactions, oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged)) return null;

          const markType = newState.schema.marks.mathExpr;
          if (!markType) return null;

          const tr = newState.tr;
          let changed = false;

          newState.doc.descendants((blockNode, blockPos) => {
            if (!blockNode.isBlock || blockNode.type.name === 'table') return;

            const tokens = [];

            blockNode.forEach((child, offset) => {
              const start = blockPos + 1 + offset;
              const end = start + child.nodeSize;

              if (MATH_NODES.has(child.type.name)) {
                tokens.push({ type: 'MATH_NODE', start, end, nodeName: child.type.name });
              } else if (child.isText && child.text) {
                const text = child.text;
                for (let i = 0; i < text.length; i++) {
                  tokens.push({
                    type: getCharType(text[i]),
                    start: start + i,
                    end: start + i + 1,
                  });
                }
              }
            });

            let currentGroup = [];

            const processGroup = (group) => {
              if (group.length === 0) return;

              let startIdx = 0;
              while (startIdx < group.length && group[startIdx].type === 'SPACE') startIdx++;

              let endIdx = group.length - 1;
              while (endIdx >= startIdx && group[endIdx].type === 'SPACE') endIdx--;

              if (startIdx > endIdx) return;

              const trimmed = group.slice(startIdx, endIdx + 1);

              // یک عبارت تنها در صورتی ریاضی است که شامل فرمول، عملگر ریاضی یا متغیر لاتین باشد
              let hasMath = false;
              for (const t of trimmed) {
                if (
                  t.type === 'MATH_NODE' ||
                  t.type === 'OPERATOR' ||
                  t.type === 'LATIN'
                ) {
                  hasMath = true;
                  break;
                }
              }

              if (hasMath) {
                const rStart = trimmed[0].start;
                const rEnd = trimmed[trimmed.length - 1].end;

                if (rEnd > rStart) {
                  let needsMark = false;
                  newState.doc.nodesBetween(rStart, rEnd, (node) => {
                    if (node.isInline && !markType.isInSet(node.marks)) {
                      needsMark = true;
                    }
                  });

                  if (needsMark) {
                    tr.addMark(rStart, rEnd, markType.create());
                    changed = true;
                  }
                }
              }
            };

            for (let i = 0; i < tokens.length; i++) {
              const token = tokens[i];
              if (token.type === 'FA_TEXT') {
                processGroup(currentGroup);
                currentGroup = [];

                // فقط در صورتی که این کاراکتر فارسی دارای مارک باشد آن را پاک کن (جلوگیری از لوپ بی‌نهایت)
                let hasMarkOnFa = false;
                newState.doc.nodesBetween(token.start, token.end, (node) => {
                  if (node.isInline && markType.isInSet(node.marks)) {
                    hasMarkOnFa = true;
                  }
                });

                if (hasMarkOnFa) {
                  tr.removeMark(token.start, token.end, markType);
                  changed = true;
                }
              } else {
                currentGroup.push(token);
              }
            }

            processGroup(currentGroup);
          });

          return changed ? tr : null;
        },
      }),
    ];
  },
});

export default MathDirectionExtension;
